import { z } from "zod";
import {
  formatContactFormFileSize,
  JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES,
} from "@/app/config/contact-form-file-limits";
import { emailSchema } from "./auth/email-schema";

export const DEALERDESK_VALUES = [
  "Verkauf Darmstadt",
  "Gebrauchtwagen",
  "Teiledienst",
  "Service",
  "Service NFZ Darmstadt",
  "Unfall",
  "Verkauf Bensheim",
  "Beitrag",
  "Kontakt Darmstadt",
  "Kontakt Bensheim",
  "Leasingvertrag",
  "Vermietung Darmstadt",
  "Charity",
  "Firmenangebot",
  "Karriere",
] as const;

export type DealerdeskT = (typeof DEALERDESK_VALUES)[number];

const dealerdeskSchema = z.enum(DEALERDESK_VALUES);

const jobOptionalDocumentSchema = z
  .union([
    z
      .custom<File>((val) => val instanceof File, {
        message: "Ungültige Datei",
      })
      .refine(
        (file: File) => {
          return file.size <= JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES;
        },
        {
          message: `Datei ist zu groß. Erlaubt sind maximal ${formatContactFormFileSize(JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES)}.`,
        }
      )
      .refine(
        (file: File) => {
          const acceptedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          ];
          return (
            acceptedTypes.includes(file.type) || file.type.startsWith("image/")
          );
        },
        { message: "Nur PDF-, Word- und Bilddateien sind erlaubt" }
      ),
    z.string().url("Ungültige Dokument-URL"),
  ])
  .optional();

const vehicleMileageSchema = z
  .preprocess(
    (value) => {
      if (value === null || value === undefined || value === "") {
        return undefined;
      }

      if (typeof value === "number") {
        return value.toString();
      }

      return value;
    },
    z
      .string()
      .trim()
      .max(50, "Kilometerstand darf höchstens 50 Zeichen enthalten")
  )
  .optional();

export const DESIRED_FINANCING_VALUES = [
  "barkauf",
  "finanzierung",
  "leasing",
] as const;

export type DesiredFinancingT = (typeof DESIRED_FINANCING_VALUES)[number];

const desiredFinancingSchema = z.enum(DESIRED_FINANCING_VALUES);

const coreSchema = z.object({
  company: z
    .string()
    .trim()
    .max(255, "Firma darf höchstens 255 Zeichen enthalten")
    .optional(),
  salutation: z.enum(["Herr", "Frau", "Divers"], "Anrede erforderlich"),
  firstname: z
    .string()
    .trim()
    .min(1, "Vorname ist erforderlich")
    .max(100, "Vorname darf höchstens 100 Zeichen enthalten")
    .regex(/^[\p{L}\s'-]+$/u, {
      message:
        "Vorname darf nur Buchstaben, Leerzeichen, Bindestriche oder Apostrophe enthalten",
    })
    .refine((value) => value.trim().length > 0, {
      message: "Vorname darf nicht nur Leerzeichen enthalten",
    }),
  lastname: z
    .string()
    .trim()
    .min(1, "Nachname ist erforderlich")
    .max(100, "Nachname darf höchstens 100 Zeichen enthalten")
    .regex(/^[\p{L}\s'-]+$/u, {
      message:
        "Nachname darf nur Buchstaben, Leerzeichen, Bindestriche oder Apostrophe enthalten",
    })
    .refine((value) => value.trim().length > 0, {
      message: "Nachname darf nicht nur Leerzeichen enthalten",
    }),
  email: emailSchema,
  phone: z
    .string()
    .trim()
    .min(1, "Telefonnummer ist erforderlich")
    .max(50, "Telefonnummer darf höchstens 50 Zeichen enthalten")
    .refine(
      (value) => {
        const cleaned = value.replace(/\s+/g, "");
        return /^\+?\d{1,15}$/.test(cleaned);
      },
      {
        message:
          "Telefonnummer darf nur Ziffern enthalten (optional mit + am Anfang, keine Leerzeichen, Klammern oder andere Zeichen)",
      }
    )
    .transform((value) => value.replace(/\s+/g, "")),
  comments: z
    .string()
    .trim()
    .max(2000, "Nachricht darf höchstens 2000 Zeichen enthalten")
    .optional(),
  location: z
    .string()
    .trim()
    .min(1, "Ungültiger Standort")
    .max(255, "Standort darf höchstens 255 Zeichen enthalten")
    .optional(),
  privacy: z.boolean().refine((value) => value === true, {
    message: "Sie müssen der Datenschutzerklärung zustimmen",
  }),
  mail: z
    .string()
    .optional()
    .default("")
    .transform((val) => val ?? ""),
  case: z.enum(["CASE", "OPPORTUNITY"], "Ungültiger Fall").default("CASE"),
  URL: z.string().optional(),
  source: z.string().default("Kontakt Anfrage"),
  dealerdesk: dealerdeskSchema.default("Kontakt Darmstadt"),
});

export const offerContactFormSchema = coreSchema
  .extend({
    source: z.string().default("Angebots Anfrage"),
    dealerdesk: dealerdeskSchema.default("Verkauf Darmstadt"),
    tradeIn: z.boolean().optional().default(false),
    make: z
      .string()
      .trim()
      .max(100, "Marke darf höchstens 100 Zeichen enthalten")
      .optional(),
    makeName: z
      .string()
      .trim()
      .max(100, "Marke darf höchstens 100 Zeichen enthalten")
      .optional(),
    aktion: z
      .string()
      .trim()
      .max(200, "Aktion darf höchstens 200 Zeichen enthalten")
      .optional(),
    vehicleCondition: z
      .string()
      .trim()
      .max(50, "Fahrzeugzustand darf höchstens 50 Zeichen enthalten")
      .optional(),
    desiredFinancing: desiredFinancingSchema.optional(),
    vehicleLicensePlate: z
      .string()
      .trim()
      .max(50, "Kennzeichen darf höchstens 50 Zeichen enthalten")
      .optional(),
    vehicleVin: z
      .string()
      .trim()
      .max(64, "FIN darf höchstens 64 Zeichen enthalten")
      .optional(),
    vehicleMileage: vehicleMileageSchema,
    vehicleModel: z
      .string()
      .trim()
      .max(100, "Modell darf höchstens 100 Zeichen enthalten")
      .optional(),
  })
  .strict();

export type OfferContactFormDataT = z.infer<typeof offerContactFormSchema>;

export const camperContactFormSchema = coreSchema
  .extend({
    source: z.string().default("Camper Anfrage"),
    dealerdesk: dealerdeskSchema.default("Service NFZ Darmstadt"),
    location: z
      .string()
      .trim()
      .min(1, "Standort ist erforderlich")
      .max(255, "Standort darf höchstens 255 Zeichen enthalten"),
    make: z
      .string()
      .trim()
      .min(1, "Marke ist erforderlich")
      .max(100, "Marke darf höchstens 100 Zeichen enthalten"),
    vehicleLicensePlate: z
      .string()
      .trim()
      .max(50, "Kennzeichen darf höchstens 50 Zeichen enthalten")
      .optional(),
    vehicleVin: z
      .string()
      .trim()
      .max(64, "FIN darf höchstens 64 Zeichen enthalten")
      .optional(),
    vehicleMileage: vehicleMileageSchema,
    vehicleModel: z
      .string()
      .trim()
      .max(100, "Modell darf höchstens 100 Zeichen enthalten")
      .optional(),
  })
  .strict();

export type CamperContactFormDataT = z.infer<typeof camperContactFormSchema>;

export const serviceContactFormSchema = coreSchema
  .extend({
    source: z.string().default("Service Anfrage"),
    dealerdesk: dealerdeskSchema.default("Service"),
    make: z
      .string()
      .trim()
      .max(100, "Marke darf höchstens 100 Zeichen enthalten")
      .optional(),
    vehicleLicensePlate: z
      .string()
      .trim()
      .max(50, "Kennzeichen darf höchstens 50 Zeichen enthalten")
      .optional(),
    vehicleVin: z
      .string()
      .trim()
      .max(64, "FIN darf höchstens 64 Zeichen enthalten")
      .optional(),
    vehicleMileage: vehicleMileageSchema,
    vehicleModel: z
      .string()
      .trim()
      .max(100, "Modell darf höchstens 100 Zeichen enthalten")
      .optional(),
  })
  .strict();

export type ServiceContactFormDataT = z.infer<typeof serviceContactFormSchema>;

export const campaignContactFormSchema = coreSchema
  .extend({
    source: z.string().default("Aktions Anfrage"),
    dealerdesk: dealerdeskSchema.default("Kontakt Darmstadt"),
    location: z
      .string()
      .trim()
      .min(1, "Wunschstandort ist erforderlich")
      .max(255, "Standort darf höchstens 255 Zeichen enthalten"),
    desiredFinancing: desiredFinancingSchema.optional(),
  })
  .strict();

export type CampaignContactFormDataT = z.infer<
  typeof campaignContactFormSchema
>;

export const standardContactFormSchema = coreSchema
  .omit({ location: true })
  .extend({
    source: z.string().default("Kontakt Anfrage"),
    dealerdesk: dealerdeskSchema.default("Kontakt Darmstadt"),
  })
  .strict();

export type StandardContactFormDataT = z.infer<
  typeof standardContactFormSchema
>;

export const companyContactFormSchema = coreSchema
  .omit({ location: true })
  .extend({
    source: z.string().default("Firmen Anfrage"),
    dealerdesk: dealerdeskSchema.default("Firmenangebot"),
    vehicleCondition: z
      .string()
      .trim()
      .max(50, "Fahrzeugzustand darf höchstens 50 Zeichen enthalten")
      .optional(),
    company: z
      .string()
      .trim()
      .max(255, "Firma darf höchstens 255 Zeichen enthalten")
      .optional(),
    abrufschein: z
      .union([
        z
          .custom<File>((val) => val instanceof File, {
            message: "Ungültige Datei",
          })
          .refine(
            (file: File) => {
              const maxSize = 10 * 1024 * 1024; // 10MB
              return file.size <= maxSize;
            },
            { message: "Datei ist zu groß. Maximale Größe: 10MB" }
          )
          .refine(
            (file: File) => {
              const acceptedTypes = [
                "application/pdf",
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/webp",
              ];
              return acceptedTypes.includes(file.type);
            },
            { message: "Nur PDF und Bilddateien sind erlaubt" }
          ),
        z.string().url("Ungültige Abrufschein-URL"),
      ])
      .optional(),
  })
  .strict();

export type CompanyContactFormDataT = z.infer<typeof companyContactFormSchema>;

export const jobContactFormSchema = coreSchema
  .omit({ location: true, company: true })
  .extend({
    source: z.string().default("Karriere Anfrage"),
    dealerdesk: dealerdeskSchema.default("Karriere"),
    phone: z
      .string()
      .trim()
      .max(50, "Telefonnummer darf höchstens 50 Zeichen enthalten")
      .refine(
        (value) => {
          if (value.length === 0) return true;
          const cleaned = value.replace(/\s+/g, "");
          return /^\+?\d{1,15}$/.test(cleaned);
        },
        {
          message:
            "Telefonnummer darf nur Ziffern enthalten (optional mit + am Anfang, keine Leerzeichen, Klammern oder andere Zeichen)",
        }
      )
      .transform((value) => {
        if (value.length === 0) return value;
        return value.replace(/\s+/g, "");
      })
      .optional(),
    zipCode: z
      .string()
      .trim()
      .min(1, "PLZ ist erforderlich")
      .max(20, "PLZ darf höchstens 20 Zeichen enthalten"),
    city: z
      .string()
      .trim()
      .min(1, "Ort ist erforderlich")
      .max(100, "Ort darf höchstens 100 Zeichen enthalten"),
    cv: jobOptionalDocumentSchema.refine((value) => Boolean(value), {
      message: "Lebenslauf ist erforderlich",
    }),
    certificates: jobOptionalDocumentSchema,
  })
  .strict();

export type JobContactFormDataT = z.infer<typeof jobContactFormSchema>;

const optionalPhoneSchema = z
  .string()
  .trim()
  .max(50, "Telefonnummer darf höchstens 50 Zeichen enthalten")
  .refine(
    (value) => {
      if (value.length === 0) return true;
      const cleaned = value.replace(/\s+/g, "");
      return /^\+?\d{1,15}$/.test(cleaned);
    },
    {
      message:
        "Telefonnummer darf nur Ziffern enthalten (optional mit + am Anfang, keine Leerzeichen, Klammern oder andere Zeichen)",
    }
  )
  .transform((value) => {
    if (value.length === 0) return value;
    return value.replace(/\s+/g, "");
  })
  .optional();

export const postContactFormSchema = coreSchema
  .omit({ company: true, phone: true })
  .extend({
    source: z.string().default("Beitrags Anfrage"),
    dealerdesk: dealerdeskSchema.default("Beitrag"),
    phone: optionalPhoneSchema,
    location: z
      .string()
      .trim()
      .min(1, "Standort ist erforderlich")
      .max(255, "Standort darf höchstens 255 Zeichen enthalten"),
  })
  .strict();

export type PostContactFormDataT = z.infer<typeof postContactFormSchema>;

export const giveawayContactFormSchema = z
  .object({
    salutation: z.enum(
      ["Herr", "Frau", "Divers", "keine-angabe"],
      "Anrede erforderlich"
    ),
    firstname: z
      .string()
      .trim()
      .min(1, "Vorname ist erforderlich")
      .max(100, "Vorname darf höchstens 100 Zeichen enthalten")
      .regex(/^[\p{L}\s'-]+$/u, {
        message:
          "Vorname darf nur Buchstaben, Leerzeichen, Bindestriche oder Apostrophe enthalten",
      })
      .refine((value) => value.trim().length > 0, {
        message: "Vorname darf nicht nur Leerzeichen enthalten",
      }),
    lastname: z
      .string()
      .trim()
      .min(1, "Nachname ist erforderlich")
      .max(100, "Nachname darf höchstens 100 Zeichen enthalten")
      .regex(/^[\p{L}\s'-]+$/u, {
        message:
          "Nachname darf nur Buchstaben, Leerzeichen, Bindestriche oder Apostrophe enthalten",
      })
      .refine((value) => value.trim().length > 0, {
        message: "Nachname darf nicht nur Leerzeichen enthalten",
      }),
    email: emailSchema,
    phone: optionalPhoneSchema,
    privacy: z.boolean().refine((value) => value === true, {
      message: "Sie müssen der Datenschutzerklärung zustimmen",
    }),
    mail: z
      .string()
      .optional()
      .default("")
      .transform((val) => val ?? ""),
    case: z.enum(["CASE", "OPPORTUNITY"], "Ungültiger Fall").default("CASE"),
    URL: z.string().optional(),
    source: z.string().default("Gewinnspiel Anfrage"),
    dealerdesk: dealerdeskSchema.default("Kontakt Darmstadt"),
  })
  .strict();

export type GiveawayContactFormDataT = z.infer<
  typeof giveawayContactFormSchema
>;

export const leasingvertragContactFormSchema = coreSchema
  .extend({
    source: z.string().default("Leasingvertrag Anfrage"),
    dealerdesk: dealerdeskSchema.default("Leasingvertrag"),
    tradeIn: z.boolean().optional().default(false),
    // Additional required fields for leasing contract
    licensePlate: z
      .string()
      .trim()
      .min(1, "Kennzeichen ist erforderlich")
      .max(50, "Kennzeichen darf höchstens 50 Zeichen enthalten"),
    make: z
      .string()
      .trim()
      .min(1, "Marke ist erforderlich")
      .max(100, "Marke darf höchstens 100 Zeichen enthalten"),
    contactPerson: z
      .string()
      .trim()
      .min(1, "Ansprechpartner ist erforderlich")
      .max(255, "Ansprechpartner darf höchstens 255 Zeichen enthalten"),
    // Trade-in vehicle fields (optional, shown when tradeIn is true)
    makeName: z
      .string()
      .trim()
      .max(100, "Marke darf höchstens 100 Zeichen enthalten")
      .optional(),
    vehicleLicensePlate: z
      .string()
      .trim()
      .max(50, "Kennzeichen darf höchstens 50 Zeichen enthalten")
      .optional(),
    vehicleVin: z
      .string()
      .trim()
      .max(64, "FIN darf höchstens 64 Zeichen enthalten")
      .optional(),
    vehicleMileage: vehicleMileageSchema,
    vehicleModel: z
      .string()
      .trim()
      .max(100, "Modell darf höchstens 100 Zeichen enthalten")
      .optional(),
  })
  .strict();

export type LeasingvertragContactFormDataT = z.infer<
  typeof leasingvertragContactFormSchema
>;
